import React from 'react';
import { MainSection } from './sections/MainSection';
import { BaseTop, BaseMain } from './sections/BaseLayerSection';
import { FaceSection } from './sections/FaceSection';
import { EarSection } from './sections/EarSection';
import { MuzzleFace } from './sections/MuzzleFaceSection';
import {
  FaceDetailStyles,
  HoodAssociateSection,
  HoodSpikeSection,
  MuzzleWhiskerSection,
  isHoodFaceDetailFamily,
} from './sections/FaceDetailSection';
import { EyebrowSection } from './sections/EyebrowSection';
import { OtherSection } from './sections/OtherSection';

/**
 * HOOD mode — full head mask (face, crown, brows, nose, muzzle, chin, ears, buttons).
 *
 * Composition order below is the SVG **z-order** (later = painted on top), which is
 * the same order the original renderer produced. It intentionally does NOT match the
 * drawer menu order — each component is named after its menu section:
 *
 *   §2 主体           MainSection
 *   (fixed)          BaseTop / BaseMain — eye borders, eyes, face seams
 *   §4 面部          FaceSection (下巴) + face colors inside FaceDetailStyles
 *   §6 耳朵细节      EarSection
 *   §5 脸部细节      FaceDetailStyles + MuzzleWhiskerSection + HoodAssociateSection + HoodSpikeSection
 *   §3 眉毛          EyebrowSection
 *   §7 其他          OtherSection (buttons, metal buttons, ear/glass holes)
 *
 * Face details (脸部细节) render from `faceDetail` (hood template — shared cross-mode
 * set) and `muzzleDetail` (muzzle template — original 胡子/鼻贴), see Hood.js.
 */
const HoodView = ({ config, parts, lines, rects, ellipses, polygons, faceDetail, muzzleDetail }) => {
  return (
    <g id="hood">
      {/* §2 主体 — 头套主体面部, 头盖 */}
      <MainSection config={config} parts={parts} />

      {/* Fixed construction — top eye border */}
      <BaseTop parts={parts} />

      {/* §4 面部 — 下巴 */}
      <FaceSection config={config} parts={parts} />

      {/* §6 耳朵细节 — A/B/C */}
      <EarSection config={config} parts={parts} />

      {/* Fixed construction — eyes + face seams */}
      <BaseMain parts={parts} />

      {/* §5 脸部细节 — 圆脸/FUR/脸颊 (also carries §4 bridge/nose/face/lip colors) */}
      <FaceDetailStyles config={config} faceDetail={faceDetail} />

      {/* §5 脸部细节 — FUR棱形/FUR胡须 (style1/style2): the muzzle-template face is
          their background (same as muzzle mode), rendered from muzzleDetail. They
          ALSO render the under-button bridge (bridge-under-button_c, from the hood
          template) so the curve under the 鼻梁纽扣 matches 圆脸/FUR. */}
      {!isHoodFaceDetailFamily(config['muzzle-style']) && (
        <>
          <MuzzleFace
            config={config}
            parts={muzzleDetail.parts}
            lines={muzzleDetail.lines}
            rects={muzzleDetail.rects}
          />
          <path
            className={"hood-" + config['bridge-color'] + " str_332C2B_20"}
            d={faceDetail.parts['bridge-under-button_c'] || ''}
          />
          <path className="clear str_222222_50" d={faceDetail.parts['bridge-under-button_seam1'] || ''}/>
          <path className="clear str_222222_25_125_75" d={faceDetail.parts['bridge-under-button_seam2'] || ''}/>
          <path className="clear str_222222_20_40_20" d={faceDetail.parts['bridge-under-button_seam3'] || ''}/>
        </>
      )}

      {/* §5 脸部细节 — original muzzle details (胡子 whiskers + 鼻贴 nose patch).
          The muzzle-template paths have the +215,-248 registration baked in, so they
          align with the hood's muzzle without a runtime transform. */}
      <MuzzleWhiskerSection config={config} muzzleDetail={muzzleDetail} />

      {/* §3 眉毛 — 双眉/豆豆/焰眉 + 眉毛颜色 */}
      <EyebrowSection config={config} parts={parts} />

      {/* §5 脸部细节 — 三孔/胡须/皮毛 */}
      <HoodAssociateSection config={config} faceDetail={faceDetail} />

      {/* §7 其他 — 纽扣 (含金属) + 加耳朵孔/加眼镜孔 */}
      <OtherSection config={config} parts={parts} ellipses={ellipses} />

      {/* §5 脸部细节 — 柳丁A/B */}
      <HoodSpikeSection config={config} faceDetail={faceDetail} />
    </g>
  );
};

export default HoodView;
